import { Dialog, DialogBackdrop, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface RightSlideOverProps {
  title: string
  content: React.ReactNode
  // Optional sticky action bar pinned to the bottom. When provided the panel
  // switches to a fixed-header / scrolling-body / pinned-footer layout. When
  // omitted the panel keeps its original whole-panel-scroll behaviour, so
  // existing callers (MessagesPanel auto-scroll, InlineHelp, AIAssistDrawer)
  // are unaffected.
  footer?: React.ReactNode
  size?: 'md' | 'lg' | 'xl'
  isOpen: boolean
  setOpen: (open: boolean) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  iconOptional?: React.ComponentType<any>
}

const sizeClass: Record<'md' | 'lg' | 'xl', string> = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

const RightSlideOver = ({ title, content, footer, size = 'md', isOpen, setOpen, iconOptional }: RightSlideOverProps) => {
  const IconComponent = iconOptional

  const header = (
    <div className="px-4 sm:px-6">
      <div className="flex items-start justify-between">
        <div className="sm:flex sm:items-start">
          {IconComponent && (
            <div
              className="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-perfect-shine-200 sm:mx-0 sm:h-10 sm:w-10">
              <IconComponent className="h-6 w-6 text-perfect-shine-800" aria-hidden="true"/>
            </div>
          )}
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <DialogTitle as="h3" className="text-lg leading-6 font-medium text-gray-900 mt-2">
              {title}
            </DialogTitle>
          </div>
        </div>
        <div className="ml-3 h-7 flex items-center">
          <button
            type="button"
            className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2"
            onClick={() => setOpen(false)}
          >
            <span className="sr-only">Close panel</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true"/>
          </button>
        </div>
      </div>
    </div>
  )

  const body = (
    <div className="text-sm text-gray-500 space-y-4 py-2">
      {content}
    </div>
  )

  return (
    <Transition show={isOpen}>
      <Dialog as="div" className="fixed z-60 inset-0 overflow-hidden" onClose={() => setOpen(false)}>
        <div className="absolute inset-0 overflow-hidden">
          {/* No animation: the drawer pops in/out (enter/leave are no-ops). */}
          <TransitionChild
            enter=""
            enterFrom="opacity-100"
            enterTo="opacity-100"
            leave=""
            leaveFrom="opacity-100"
            leaveTo="opacity-100"
          >
            <DialogBackdrop className="absolute inset-0 bg-ink/30" data-testid="slideover-backdrop"/>
          </TransitionChild>

          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <TransitionChild
              enter=""
              enterFrom="translate-x-0"
              enterTo="translate-x-0"
              leave=""
              leaveFrom="translate-x-0"
              leaveTo="translate-x-0"
            >
              <div className={`w-screen ${sizeClass[size]}`}>
                {footer ? (
                  <div className="h-full flex flex-col bg-white border">
                    <div className="pt-6 shrink-0">{header}</div>
                    <div className="mt-3 flex-1 overflow-y-auto px-4 sm:px-6">
                      {body}
                    </div>
                    <div className="shrink-0 border-t border-gray-200 px-4 sm:px-6 py-4 bg-white">
                      {footer}
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex flex-col py-6 bg-white border overflow-y-scroll">
                    {header}
                    <div className="mt-3 relative flex-1 px-4 sm:px-6">
                      {body}
                    </div>
                  </div>
                )}
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default RightSlideOver
